import React from "react";
import { Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import type { StrategyRdManageRule } from "../../../types/strategyRd";
import { strategyRdManageErrors } from "../../../utils/strategyRdRuleEditor";
import { SmallNumber } from "./StrategyRdRuleEditor";
import { StrategyRdSettingsSection } from "./strategyRdLabUi";

const TRAIL_KIND_OPTIONS = [
  { value: "atr", label: "ATR from the best price" },
  { value: "swing", label: "last swing" },
];

function Row({
  on,
  onToggle,
  label,
  error,
  children,
  id,
}: {
  on: boolean;
  onToggle: (on: boolean) => void;
  label: string;
  error?: string | null;
  children?: React.ReactNode;
  id: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1" data-strategy-rd-manage={id}>
      <div className="flex min-h-8 flex-wrap items-center gap-1.5 text-sm">
        <Switch checked={on} aria-label={label} onCheckedChange={onToggle} />
        <span className={on ? "text-foreground" : "text-muted-foreground"}>{label}</span>
        {on ? children : null}
      </div>
      {on && error ? <p className="pl-10 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function Word({ children }: { children: React.ReactNode }): JSX.Element {
  return <span className="text-muted-foreground">{children}</span>;
}

/** Break-even, trailing stop, partial profits and time stop (`template.manage`). */
export function StrategyRdManageTradeSection({
  manage,
  onChange,
  serverError = null,
  myRules = null,
  step,
}: {
  /** Position in a numbered settings form. */
  step?: number;
  manage: StrategyRdManageRule | null | undefined;
  onChange: (next: StrategyRdManageRule | null) => void;
  /** 400 `strategy_rd.invalid_template` about management, shown as sent. */
  serverError?: string | null;
  /** "My rules" menu for management sets. */
  myRules?: React.ReactNode;
}): JSX.Element {
  const m = manage ?? {};
  const errors = strategyRdManageErrors(m);
  const set = (patch: Partial<StrategyRdManageRule>) => {
    const next = { ...m, ...patch };
    const active = Object.values(next).some((v) => v != null && !(Array.isArray(v) && v.length === 0));
    onChange(active ? next : null);
  };
  const partials = m.partials ?? [];
  const be = m.break_even;
  const trail = m.trail;

  return (
    <StrategyRdSettingsSection
      step={step}
      title="Manage the trade"
      actions={myRules}
      description="Move the stop, trail it, take partials or close after a while. Stops move at a candle's close and apply from the next candle, as on TradingView; a partial past the target never fills."
      data-strategy-rd-manage-section=""
    >
      <div className="flex flex-col gap-2">

      <Row
        id="break_even"
        label="Move stop to break-even after"
        on={Boolean(be)}
        onToggle={(on) => set({ break_even: on ? { after_r: 1, lock_r: 0 } : null })}
        error={errors.break_even}
      >
        {be ? (
          <>
            <SmallNumber
              ariaLabel="Break-even after R"
              value={be.after_r}
              min={0}
              max={20}
              onCommit={(after_r) => set({ break_even: { ...be, after_r } })}
            />
            <Word>R, locking</Word>
            <SmallNumber
              ariaLabel="Lock R"
              value={be.lock_r}
              min={-1}
              max={10}
              onCommit={(lock_r) => set({ break_even: { ...be, lock_r } })}
            />
            <Word>R</Word>
          </>
        ) : null}
      </Row>

      <Row
        id="trail"
        label="Trail the stop:"
        on={Boolean(trail)}
        onToggle={(on) => set({ trail: on ? { kind: "atr", mult: 2 } : null })}
        error={errors.trail}
      >
        {trail ? (
          <>
            <FieldSelectFit
              value={trail.kind}
              options={TRAIL_KIND_OPTIONS}
              onValueChange={(kind) => set({ trail: { ...trail, kind: kind as "atr" | "swing" } })}
            />
            <Word>{trail.kind === "swing" ? "minus" : "×"}</Word>
            <SmallNumber
              ariaLabel="Trail multiple"
              value={trail.mult}
              min={0}
              max={20}
              onCommit={(mult) => set({ trail: { ...trail, mult } })}
            />
            <Word>{trail.kind === "swing" ? "× ATR, start after" : "ATR, start after"}</Word>
            <OptionalR
              value={trail.after_r ?? null}
              onCommit={(after_r) => set({ trail: { ...trail, after_r } })}
            />
            <Word>R (blank = at once)</Word>
          </>
        ) : null}
      </Row>

      <Row
        id="partials"
        label="Take partial profits"
        on={partials.length > 0}
        onToggle={(on) => set({ partials: on ? [{ at_r: 1, close_pct: 50 }] : null })}
        error={errors.partials}
      />
      {partials.length > 0 ? (
        <div className="flex flex-col gap-1 pl-10">
          {partials.map((p, i) => (
            <div key={i} className="flex flex-wrap items-center gap-1.5 text-sm">
              <Word>close</Word>
              <SmallNumber
                ariaLabel="Close percent"
                value={p.close_pct}
                min={1}
                max={99}
                onCommit={(close_pct) =>
                  set({ partials: partials.map((q, j) => (j === i ? { ...q, close_pct } : q)) })
                }
              />
              <Word>% at</Word>
              <SmallNumber
                ariaLabel="At R"
                value={p.at_r}
                min={0}
                max={50}
                onCommit={(at_r) =>
                  set({ partials: partials.map((q, j) => (j === i ? { ...q, at_r } : q)) })
                }
              />
              <Word>R</Word>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label="Remove partial"
                onClick={() => set({ partials: partials.filter((_, j) => j !== i) })}
              >
                <X className="size-3.5" aria-hidden />
              </Button>
            </div>
          ))}
          {partials.length < 3 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-fit px-2 text-xs"
              onClick={() => {
                const last = partials[partials.length - 1];
                set({ partials: [...partials, { at_r: (last?.at_r ?? 0) + 1, close_pct: 25 }] });
              }}
            >
              <Plus className="size-3" aria-hidden /> partial
            </Button>
          ) : null}
        </div>
      ) : null}

      <Row
        id="time_stop_bars"
        label="Close after"
        on={m.time_stop_bars != null}
        onToggle={(on) => set({ time_stop_bars: on ? 12 : null })}
        error={errors.time_stop_bars}
      >
        {m.time_stop_bars != null ? (
          <>
            <SmallNumber
              ariaLabel="Bars"
              value={m.time_stop_bars}
              min={1}
              step={1}
              onCommit={(n) => set({ time_stop_bars: Math.max(1, Math.floor(n)) })}
            />
            <Word>bars</Word>
          </>
        ) : null}
      </Row>

      <Row
        id="exit_at"
        label="Close by"
        on={m.exit_at != null}
        onToggle={(on) => set({ exit_at: on ? { time: "16:00", tz: "America/New_York" } : null })}
        error={errors.exit_at}
      >
        {m.exit_at != null ? (
          <>
            <input
              type="time"
              aria-label="Close time"
              value={m.exit_at.time}
              onChange={(e) => e.target.value && set({ exit_at: { ...m.exit_at!, time: e.target.value } })}
              className="h-7 rounded-md border border-border bg-background px-2 text-xs tabular-nums"
            />
            <select
              aria-label="Time zone"
              value={m.exit_at.tz}
              onChange={(e) => set({ exit_at: { ...m.exit_at!, tz: e.target.value } })}
              className="h-7 rounded-md border border-border bg-background px-1.5 text-xs"
            >
              {EXIT_ZONES.map(([tz, label]) => (
                <option key={tz} value={tz}>
                  {label}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </Row>

      {serverError ? <p className="text-xs text-destructive">{serverError}</p> : null}
      </div>
    </StrategyRdSettingsSection>
  );
}

/** Session-end time zones for "Close by" (daylight saving included). */
const EXIT_ZONES: Array<[string, string]> = [
  ["America/New_York", "New York"],
  ["Europe/London", "London"],
  ["Europe/Berlin", "Frankfurt"],
  ["Asia/Tokyo", "Tokyo"],
  ["Australia/Sydney", "Sydney"],
  ["UTC", "UTC"],
];

/** Blank = null (e.g. trail "start after" — omit = at once). */
function OptionalR({
  value,
  onCommit,
}: {
  value: number | null;
  onCommit: (next: number | null) => void;
}): JSX.Element {
  const [text, setText] = React.useState(value == null ? "" : String(value));
  const [focused, setFocused] = React.useState(false);
  React.useEffect(() => {
    if (!focused) setText(value == null ? "" : String(value));
  }, [focused, value]);
  return (
    <input
      aria-label="Start after R"
      inputMode="decimal"
      className="h-8 w-14 rounded-[6px] border border-input bg-transparent px-2 text-sm dark:bg-input/30"
      value={text}
      placeholder="N/A"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value;
        if (raw !== "" && !/^\d*\.?\d*$/.test(raw)) return;
        setText(raw);
        if (raw.trim() === "") onCommit(null);
        else if (Number.isFinite(Number(raw)) && !raw.endsWith(".")) onCommit(Number(raw));
      }}
    />
  );
}
